
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import GeneralSettingsSection from './settings/GeneralSettingsSection';
import UserManagementSection from './settings/UserManagementSection';
import NotificationSettingsSection from './settings/NotificationSettingsSection';
import UIContentSettingsSection from './settings/UIContentSettingsSection';

interface AdminSettingsType {
  siteName: string;
  supportEmail: string;
  maintenanceMode: boolean;
  userRegistration: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  autoApproveListings: boolean;
  requireKYC: boolean;
  showRecommendedSection: boolean;
}

const AdminSettings = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<AdminSettingsType>({
    siteName: 'Maskan',
    supportEmail: 'support@maskan.com',
    maintenanceMode: false,
    userRegistration: true,
    emailNotifications: true,
    smsNotifications: false,
    autoApproveListings: false,
    requireKYC: true,
    showRecommendedSection: true
  });

  // Load settings from database on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      console.log('Loading admin settings...');
      const { data, error } = await supabase
        .from('config')
        .select('key, value')
        .in('key', [
          'site_name',
          'support_email',
          'maintenance_mode',
          'user_registration',
          'email_notifications',
          'sms_notifications',
          'auto_approve_listings',
          'require_kyc',
          'show_recommended_section'
        ]);

      if (error) {
        console.error('Error loading settings:', error);
        toast({
          title: t('error', 'خطأ', 'Error'),
          description: t('settings_load_error', 'حدث خطأ أثناء تحميل الإعدادات', 'An error occurred while loading settings'),
          variant: 'destructive'
        });
        return;
      }

      console.log('Loaded config data:', data);

      if (data && data.length > 0) {
        const loadedSettings = { ...settings };
        
        data.forEach(item => {
          switch (item.key) {
            case 'site_name':
              loadedSettings.siteName = String(item.value);
              break;
            case 'support_email':
              loadedSettings.supportEmail = String(item.value);
              break;
            case 'maintenance_mode':
              loadedSettings.maintenanceMode = Boolean(item.value);
              break;
            case 'user_registration':
              loadedSettings.userRegistration = Boolean(item.value);
              break;
            case 'email_notifications':
              loadedSettings.emailNotifications = Boolean(item.value);
              break;
            case 'sms_notifications':
              loadedSettings.smsNotifications = Boolean(item.value);
              break;
            case 'auto_approve_listings':
              loadedSettings.autoApproveListings = Boolean(item.value);
              break;
            case 'require_kyc':
              loadedSettings.requireKYC = Boolean(item.value);
              break;
            case 'show_recommended_section':
              loadedSettings.showRecommendedSection = Boolean(item.value);
              break;
          }
        });
        
        setSettings(loadedSettings);
        console.log('Settings loaded successfully:', loadedSettings);
      } else {
        console.log('No settings found in database, using defaults');
      }
    } catch (error) {
      console.error('Exception loading settings:', error);
      toast({
        title: t('error', 'خطأ', 'Error'),
        description: t('settings_load_error', 'حدث خطأ أثناء تحميل الإعدادات', 'An error occurred while loading settings'),
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('Saving admin settings...', settings);

      // Prepare the settings data for upsert
      const settingsToSave = [
        { key: 'site_name', value: settings.siteName },
        { key: 'support_email', value: settings.supportEmail },
        { key: 'maintenance_mode', value: settings.maintenanceMode },
        { key: 'user_registration', value: settings.userRegistration },
        { key: 'email_notifications', value: settings.emailNotifications },
        { key: 'sms_notifications', value: settings.smsNotifications },
        { key: 'auto_approve_listings', value: settings.autoApproveListings },
        { key: 'require_kyc', value: settings.requireKYC },
        { key: 'show_recommended_section', value: settings.showRecommendedSection }
      ];

      // Use upsert to insert or update each setting
      for (const setting of settingsToSave) {
        console.log(`Saving setting: ${setting.key} = ${setting.value}`);
        
        const { error } = await supabase
          .from('config')
          .upsert({
            key: setting.key,
            value: setting.value,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'key'
          });

        if (error) {
          console.error(`Error saving setting ${setting.key}:`, error);
          throw error;
        }
      }

      console.log('All settings saved successfully');
      
      toast({
        title: t('settings_saved', 'تم حفظ الإعدادات', 'Settings Saved'),
        description: t('settings_saved_desc', 'تم حفظ إعدادات النظام بنجاح', 'System settings have been saved successfully')
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: t('error', 'خطأ', 'Error'),
        description: t('settings_save_error', 'حدث خطأ أثناء حفظ الإعدادات', 'An error occurred while saving settings'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key: keyof AdminSettingsType, value: any) => {
    console.log(`Updating setting: ${key} = ${value}`);
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6">
        <GeneralSettingsSection
          settings={{
            siteName: settings.siteName,
            supportEmail: settings.supportEmail,
            maintenanceMode: settings.maintenanceMode
          }}
          updateSetting={updateSetting}
        />

        <UserManagementSection
          settings={{
            userRegistration: settings.userRegistration,
            requireKYC: settings.requireKYC,
            autoApproveListings: settings.autoApproveListings
          }}
          updateSetting={updateSetting}
        />

        <NotificationSettingsSection
          settings={{
            emailNotifications: settings.emailNotifications,
            smsNotifications: settings.smsNotifications
          }}
          updateSetting={updateSetting}
        />

        <UIContentSettingsSection
          settings={{
            showRecommendedSection: settings.showRecommendedSection
          }}
          updateSetting={updateSetting}
        />

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={loading} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            {loading ? t('saving', 'جاري الحفظ...', 'Saving...') : t('save_settings', 'حفظ الإعدادات', 'Save Settings')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
